"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { Section } from "@/components/layout/Section"
import { Button } from "@/components/ui/button"
import { 
  Briefcase, 
  ArrowRight,
  TrendingUp,
  Heart,
  Coffee,
  Users,
} from "lucide-react"
import Link from "next/link"
import { CTA } from "@/components/sections/CTA"
import { usePathname } from "next/navigation"
import { isValidLocale, type Locale } from "@/lib/i18n"
import { getTranslations } from "@/lib/translations"

export default function CareerPage() {
  const pathname = usePathname()
  
  // Get current locale from pathname
  const getCurrentLocale = (): Locale => {
    const segments = pathname.split('/').filter(Boolean)
    const firstSegment = segments[0]
    return isValidLocale(firstSegment) ? firstSegment : 'tr'
  }

  const currentLocale = getCurrentLocale()
  const t = getTranslations(currentLocale)

  const benefits = t.pages.career.whyJoin.items.map((item, index) => {
    const icons = [TrendingUp, Heart, Coffee, Users]
    return {
      icon: icons[index],
      title: item.title,
      description: item.description,
    }
  })

  return (
    <>
      {/* Hero Section */}
      <Section className="relative overflow-hidden bg-gradient-to-br from-[rgb(3,74,166)] via-[rgb(3,74,166)] to-[rgb(2,40,89)] text-white pt-24 pb-16 sm:pt-32 sm:pb-24">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_70%)]" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex p-4 bg-white/10 backdrop-blur-sm rounded-2xl mb-8"
          >
            <Briefcase className="h-12 w-12 sm:h-16 sm:w-16" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 font-mono"
          >
            {t.pages.career.hero.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto"
          >
            {t.pages.career.hero.subtitle}
          </motion.p>
        </div>
      </Section>

      {/* Why Join Us Section */}
      <Section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-6 font-mono">
              {t.pages.career.whyJoin.title}
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              {t.pages.career.whyJoin.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-all duration-300"
                >
                  <div className="inline-flex p-3 bg-[rgb(3,74,166)]/10 rounded-lg mb-4">
                    <Icon className="h-6 w-6 text-[rgb(3,74,166)]" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2 font-mono">{benefit.title}</h3>
                  <p className="text-slate-600 text-sm">{benefit.description}</p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </Section>

      {/* Job Openings Section (temporarily without listings) */}
      <Section className="py-16 lg:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-6 font-mono">
              {t.pages.career.openings.title}
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              {t.pages.career.openings.noOpenings}
            </p>
            <p className="text-sm text-slate-500 mt-2 max-w-2xl mx-auto">
              {currentLocale === 'tr' 
                ? 'Yine de ekibimize katılmak isterseniz, aşağıdaki genel başvuru formunu doldurabilirsiniz.'
                : 'If you would still like to join our team, you can fill out the general application form below.'}
            </p>
          </div>

          {/* General Application */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-gradient-to-br from-[rgb(3,74,166)] to-[rgb(2,40,89)] rounded-xl p-8 text-white text-center"
          >
            <h3 className="text-2xl font-bold mb-4 font-mono">{t.pages.career.openings.generalApplication.title}</h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              {t.pages.career.openings.generalApplication.description}
            </p>
            <Button
              asChild
              size="lg"
              className="bg-white text-[rgb(3,74,166)] hover:bg-slate-100"
            >
              <Link href={`/${currentLocale}/${currentLocale === 'tr' ? 'kariyer/basvuru' : 'career/application'}`} className="flex items-center gap-2">
                {t.pages.career.openings.generalApplication.button}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </Section>
      <CTA locale={currentLocale} />
    </>
  )
}

